import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Box
} from '@mui/material';
import PermissionMatrix from './PermissionMatrix';
import api from '../services/api';

const RoleForm = ({ open, onClose, onSuccess, role = null }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    permissions: []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (role) {
      setFormData({
        name: role.name || '',
        description: role.description || '',
        permissions: role.permissions || []
      });
    } else {
      setFormData({
        name: '',
        description: '',
        permissions: []
      });
    }
    setError('');
  }, [role, open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePermissionsChange = (newPermissions) => {
    setFormData(prev => ({
      ...prev,
      permissions: newPermissions
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (role) {
        // Update existing role
        await api.put(`/api/roles/${role.id}`, formData);
      } else {
        // Create new role
        await api.post('/api/roles', formData);
      }

      onSuccess();
      onClose();
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to save role');
    } finally {
      setLoading(false);
    }
  };

  const isSystemRole = role?.is_system;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '70vh' }
      }}
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          {role ? 'Edit Role' : 'Create New Role'}
        </DialogTitle>

        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {isSystemRole && (
            <Alert severity="info" sx={{ mb: 2 }}>
              This is a system role. You can only modify permissions, not the name or description.
            </Alert>
          )}

          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              label="Role Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              disabled={isSystemRole}
              margin="normal"
              helperText={isSystemRole ? 'System role names cannot be changed' : ''}
            />

            <TextField
              fullWidth
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              multiline
              rows={2}
              margin="normal"
              disabled={isSystemRole && false} // Allow description edits
            />
          </Box>

          <PermissionMatrix
            permissions={formData.permissions}
            onChange={handlePermissionsChange}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={loading && <CircularProgress size={20} />}
          >
            {role ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default RoleForm;
